<template>
  <a-typography-title :level="4">
    {{ props.rule ? 'Editar' : 'Crear' }} regla de notificación
  </a-typography-title>

  <a-alert v-if="props.error" type="error" :message="props.error" />

  <a-alert v-if="props.success" type="success" :message="props.success" />

  <a-form
    ref="formRef"
    :model="formState"
    layout="vertical"
    style="margin-top: 16px"
  >
    <a-row :gutter="16">
      <a-col :xs="24" :md="24">
        <a-form-item
          name="companyNotificationTypeId"
          :rules="{
            message: 'Seleccione un tipo de notificación',
            trigger: 'change',
            type: 'number'
          }"
          label="Notificación"
        >
          <SelectOrdered
            placeholder="Seleccione una opción"
            v-model="formState.companyNotificationTypeId"
            :options="typeOptions"
            :loading="loading"
            :disabled="submitting"
          />
        </a-form-item>
      </a-col>
      <a-col :xs="24" :md="12">
        <a-form-item
          name="groupCompanyId"
          :rules="{
            message: 'Seleccione un grupo',
            trigger: 'change',
            type: 'number'
          }"
          label="Grupo"
        >
          <SelectOrdered
            placeholder="Seleccione una opción"
            v-model="formState.groupCompanyId"
            :options="groupOptions"
            :loading="loading"
            :disabled="submitting"
          />
        </a-form-item>
      </a-col>
      <a-col :xs="24" :md="12">
        <a-form-item
          name="policyId"
          :rules="{
            message: 'Seleccione un póliza',
            trigger: 'change',
            type: 'number'
          }"
          label="Póliza"
        >
          <SelectOrdered
            placeholder="Seleccione una opción"
            v-model="formState.policyId"
            :options="policyOptions"
            :loading="loading"
            :disabled="submitting"
          />
        </a-form-item>
      </a-col>
      <a-col :xs="24" :md="24">
        <a-form-item label="Prioridad">
          <a-alert type="info" show-icon style="margin-bottom: 8px">
            <template #message>
              <b>Jerarquía de prioridades:</b>
              Grupo + Póliza > Póliza > Grupo > Global
            </template>
          </a-alert>
          <NotificationRuleTag :label="priorityLabel" :color="priorityColor" />
        </a-form-item>
      </a-col>
      <a-col :xs="24" :md="24">
        <a-form-item
          :name="['emails', index, 'value']"
          v-for="(email, index) in formState.emails"
          :key="index"
          :rules="{
            required: true,
            message: 'Ingresar un email válido',
            trigger: 'change',
            type: 'email'
          }"
          :label="index === 0 ? 'Destinatarios' : ''"
        >
          <div style="display: flex; align-items: center; gap: 8px">
            <InputEmail
              v-model="email.value"
              placeholder="email@ejemplo.cl"
              :disabled="submitting"
            />
            <a-button
              v-if="formState.emails.length > 1"
              @click="removeEmail(index)"
              class="text-error"
              :disabled="submitting"
            >
              <template #icon>
                <CloseOutlined />
              </template>
            </a-button>
          </div>
        </a-form-item>
        <a-button @click="addEmail" :disabled="submitting">
          <template #icon>
            <PlusOutlined />
          </template>
          Agregar correo
        </a-button>
      </a-col>
    </a-row>
    <div
      style="
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
        gap: 8px;
      "
    >
      <a-button @click="handleCancel" :disabled="submitting">Cancelar</a-button>
      <a-button
        type="primary"
        @click="onSubmit"
        :loading="submitting"
        :disabled="submitting"
      >
        {{ props.rule ? 'Actualizar' : 'Guardar' }} regla
      </a-button>
    </div>
  </a-form>
</template>

<script setup>
import {
  computed,
  reactive,
  ref,
  watch,
  defineProps,
  defineEmits,
  defineExpose
} from 'vue';
import SelectOrdered from '@/bewell-components/form-controls/select-ordered.vue';
import InputEmail from '@/bewell-components/form-controls/input-email.vue';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons-vue';
import NotificationRuleTag from '../ui/notification-rule-tag.vue';

const props = defineProps({
  rule: {
    type: Object,
    default: null
  },
  types: {
    type: Array,
    required: true
  },
  groups: {
    type: Array,
    required: true
  },
  policies: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  submitting: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  success: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['save', 'cancel']);

const formRef = ref();

const initialFormState = {
  companyNotificationTypeId: null,
  groupCompanyId: null,
  policyId: null,
  emails: [{ value: '' }]
};
const formState = reactive({ ...initialFormState });

const populateFormFromRule = (rule) => {
  if (!rule) resetForm();

  Object.assign(formState, {
    companyNotificationTypeId: rule.companyNotificationTypeId,
    groupCompanyId: rule.groupCompanyId || null,
    policyId: rule.policyId || null,
    emails: rule.emails?.map((email) => ({ value: email })) || [{ value: '' }]
  });
};

watch(() => props.rule, populateFormFromRule, { immediate: true });

watch(
  () => props.success,
  async (newSuccess) => {
    if (newSuccess) {
      await resetForm();
    }
  }
);

const typeOptions = computed(() => {
  return props.types.map((type) => ({
    label: type.description,
    value: type.id
  }));
});

const groupOptions = computed(() => {
  return props.groups.map((group) => ({
    label: group.name,
    value: group.id
  }));
});

const policyOptions = computed(() => {
  return props.policies.map((policy) => ({
    label: policy.numberPolicy,
    value: policy.id
  }));
});

const priorityLabel = computed(() => {
  const { policyId, groupCompanyId } = formState;
  if (policyId && groupCompanyId) return 'Póliza + Grupo';
  if (policyId) return 'Póliza';
  if (groupCompanyId) return 'Grupo';
  return 'Global';
});

const priorityColor = computed(() => {
  const { policyId, groupCompanyId } = formState;
  if (policyId && groupCompanyId) return 'red';
  if (policyId) return 'orange';
  if (groupCompanyId) return 'green';
  return 'blue';
});

const addEmail = () => {
  formState.emails.push({ value: '' });
};
const removeEmail = (index) => {
  if (formState.emails.length > 1) {
    formState.emails.splice(index, 1);
  }
};

const resetForm = async () => {
  await formRef.value.resetFields();
  Object.assign(formState, { ...initialFormState });
};

const handleCancel = async () => {
  await resetForm();
  emit('cancel');
};

const onSubmit = async () => {
  try {
    await formRef.value.validate();

    const emails = formState.emails
      .map((email) => email.value?.trim())
      .filter((email) => email && email.length > 0);

    if (emails.length === 0) {
      throw new Error('Al menos un email es requerido');
    }

    const formData = {
      ...formState,
      emails
    };

    emit('save', formData);
  } finally {
    console.log('Form submitted:', formState);
  }
};

defineExpose({
  resetForm
});
</script>
